import { DetailTable, PeriodKind } from "./DetailTable";
import { useFinancialHighlightsData } from "../hooks/useFinancialHighlightsData";
import { JumpToSection } from "./JumpToSection";
import { MemoTable } from "./MemoTable";
import { useFinanceLab } from "@/entities/FinanceLab";
import { useFlags } from "@/utils/flagsmith/useFlags";
import { InfoTooltip } from "./InfoTooltip";
import { useAtomValue } from "jotai";
import {
    financialHighlightsAsOfDateAtom,
    projectionsAsOfDateAtom,
} from "../metrics.state";
import { formatAsOfDate } from "../utils/formatAsOfDate";

export function FinancialHighlights() {
    const flags = useFlags();
    const showM05 = flags.enabled("show-im-m05");
    const showProjectionMetrics = flags.enabled("show-projection-metrics");
    const {
        incomeStatementData,
        balanceSheetData,
        capitalStructureData,
        projectedFinancialsData,
        projectedFinancialsM05Data,
        postClosingMetricsData,
    } = useFinancialHighlightsData();

    const { isPolling } = useFinanceLab();
    const asOfDate = useAtomValue(financialHighlightsAsOfDateAtom);
    const projectionsAsOfDate = useAtomValue(projectionsAsOfDateAtom);

    const isLoadingMetrics = isPolling;

    const formattedAsOfDate = formatAsOfDate(asOfDate);
    const formattedProjectionsAsOfDate = formatAsOfDate(projectionsAsOfDate);

    const projectedFinancialsColumns = [
        { field: "metric", headerName: "", flex: 1.5, minWidth: 200 },
        { field: "2026", headerName: "2026", flex: 1, minWidth: 120 },
        { field: "2027", headerName: "2027", flex: 1, minWidth: 120 },
        { field: "2028", headerName: "2028", flex: 1, minWidth: 120 },
    ];

    return (
        <>
            <div className="space-y-6">
                <div className="flex flex-col gap-2">
                    <div className="body-s flex items-center gap-1">
                        <span className="subdued italic">
                            Data as of {formattedAsOfDate}
                        </span>
                        <InfoTooltip>
                            The data displayed is from the last closed, complete
                            calendar period available in the company&apos;s
                            accounting system. Hum does not display partial
                            period data.
                        </InfoTooltip>
                    </div>
                    <p className="body-s">
                        Income statement and Projections are presented Annually,
                        Balance Sheet data is presented Monthly. Total Equity
                        Raised is not effected by timeframe. Click any metric to
                        see an interactive deepdive in the Financials and
                        Projections section.
                    </p>
                </div>
                <DetailTable
                    title="Income Statement"
                    data={incomeStatementData}
                    isLoading={isLoadingMetrics}
                    periodKind={PeriodKind.LTM}
                />
                <DetailTable
                    title="Balance Sheet"
                    data={balanceSheetData}
                    isLoading={isLoadingMetrics}
                    periodKind={PeriodKind.LAST_CLOSED_MONTH}
                />
                <DetailTable
                    title="Capital Structure"
                    data={capitalStructureData}
                    isLoading={isLoadingMetrics}
                />
                {showProjectionMetrics && (
                    <>
                        {showM05 ? (
                            <DetailTable
                                title="Projected Financials"
                                data={projectedFinancialsM05Data}
                                isLoading={isLoadingMetrics}
                                asOfDate={formattedProjectionsAsOfDate}
                                periodKind={PeriodKind.NTM}
                            />
                        ) : (
                            <div>
                                <h4 className="heading-m mb-4">
                                    Projected Financials Table (next 3 years)
                                </h4>
                                <MemoTable
                                    data={projectedFinancialsData}
                                    columns={projectedFinancialsColumns}
                                />
                            </div>
                        )}

                        <DetailTable
                            title="Projected Post-Closing Metrics"
                            data={postClosingMetricsData}
                            isLoading={isLoadingMetrics}
                            asOfDate="Target Close Date"
                            periodKind={PeriodKind.NTM}
                        />
                    </>
                )}
            </div>
            <JumpToSection
                targetSection="financial-highlights"
                label="Jump to Financials & Projections"
            />
        </>
    );
}
